import { API } from "@/services/api";
import { IAPIResponse } from "@/services/api.types";
import { dealEndpoints } from "@/constant/endpoints";
import { buildQueryString } from "@/utils/common.utils";
import type { Audience } from "@/enum/common.enum";
import {
  Appointment,
  AppointmentAttendee,
  AppointmentPayload,
  Activity,
  ActivityFormData,
  ClientSummaryResponse,
  Deal,
  DealDetail,
  DealEmailListResponse,
  DealFormData,
  DealExportResponse,
  DealListParams,
  DealListResponse,
  DealStats,
  DealUpdatePayload,
  DealDocument,
  DocumentFormData,
  GalleryAddFormData,
  MyDealsResponse,
  ParticipantFormData,
  PublicDealResponse,
  Task,
  TaskFormData,
  TaskListParams,
  TaskListResponse,
  TaskUpdatePayload,
} from "@/types/deals.types";

// ============ Deals ============
export const getDealsService = async (params: DealListParams = {}): Promise<DealListResponse> => {
  const queryString = buildQueryString(params);
  const response = await API.Get<DealListResponse>(`${dealEndpoints.deals}${queryString}`);
  if (response.data) return response.data;
  throw new Error(response.message);
};

export const getMyDealsService = async (): Promise<MyDealsResponse> => {
  const response = await API.Get<MyDealsResponse>(dealEndpoints.myDeals);
  if (response.data) return response.data;
  throw new Error(response.message);
};

export const createDealService = async (payload: DealFormData): Promise<IAPIResponse<Deal>> => {
  return API.Post<DealFormData, Deal>(dealEndpoints.deals, payload);
};

export const getDealsExportService = async (params: DealListParams = {}): Promise<DealExportResponse> => {
  const queryString = buildQueryString(params);
  const response = await API.Get<DealExportResponse>(`${dealEndpoints.dealsExport}${queryString}`);
  if (response.data) return response.data;
  throw new Error(response.message);
};

export const getDealStatsService = async (dealId: string): Promise<DealStats> => {
  const response = await API.Get<DealStats>(dealEndpoints.dealStats(dealId));
  if (response.data) return response.data;
  throw new Error(response.message);
};

export const getDealService = async (dealId: string): Promise<DealDetail> => {
  const response = await API.Get<DealDetail>(dealEndpoints.dealById(dealId));
  if (response.data) return response.data;
  throw new Error(response.message);
};

export const getDealClientSummaryService = async (dealId: string): Promise<ClientSummaryResponse> => {
  const response = await API.Get<ClientSummaryResponse>(dealEndpoints.dealClientSummary(dealId));
  if (response.data) return response.data;
  throw new Error(response.message);
};

export const updateDealService = async (
  dealId: string,
  payload: DealUpdatePayload
): Promise<IAPIResponse<Deal>> => {
  return API.Patch<DealUpdatePayload, Deal>(dealEndpoints.dealById(dealId), payload);
};

export const archiveDealService = async (dealId: string): Promise<IAPIResponse<{ ok: boolean; archived_at: string }>> => {
  return API.Post<undefined, { ok: boolean; archived_at: string }>(dealEndpoints.archiveDeal(dealId));
};

export const cancelDealService = async (dealId: string): Promise<IAPIResponse<{ ok: boolean; cancelled_at: string }>> => {
  return API.Post<{ reason: string }, { ok: boolean; cancelled_at: string }>(dealEndpoints.cancelDeal(dealId), { reason: "" });
};

export const unarchiveDealService = async (dealId: string): Promise<IAPIResponse<{ ok: boolean }>> => {
  return API.Post<undefined, { ok: boolean }>(dealEndpoints.unarchiveDeal(dealId));
};

export const deleteDealService = async (dealId: string, confirm: string): Promise<IAPIResponse<{ ok: boolean }>> => {
  const queryString = buildQueryString({ confirm });
  return API.Delete<undefined, { ok: boolean }>(`${dealEndpoints.dealById(dealId)}${queryString}`);
};

// ============ Gallery ============
export const addToGalleryService = async (
  dealId: string,
  payload: GalleryAddFormData
): Promise<IAPIResponse<{ ok: boolean; url: string }>> => {
  return API.Post<GalleryAddFormData, { ok: boolean; url: string }>(dealEndpoints.dealGallery(dealId), payload);
};

export const removeFromGalleryService = async (
  dealId: string,
  url: string
): Promise<IAPIResponse<{ ok: boolean }>> => {
  const queryString = buildQueryString({ url });
  return API.Delete<undefined, { ok: boolean }>(`${dealEndpoints.dealGallery(dealId)}${queryString}`);
};

// ============ Participants ============
export const addParticipantService = async (
  dealId: string,
  payload: ParticipantFormData
): Promise<IAPIResponse<{ ok: boolean }>> => {
  return API.Post<ParticipantFormData, { ok: boolean }>(dealEndpoints.dealParticipants(dealId), payload);
};

// ============ Tasks ============
export const getTasksService = async (
  dealId: string,
  params: TaskListParams = {}
): Promise<TaskListResponse> => {
  const queryString = buildQueryString(params);
  const response = await API.Get<TaskListResponse>(`${dealEndpoints.dealTasks(dealId)}${queryString}`);
  if (response.data) return response.data;
  throw new Error(response.message);
};

export const createTaskService = async (
  dealId: string,
  payload: TaskFormData
): Promise<IAPIResponse<Task>> => {
  return API.Post<TaskFormData, Task>(dealEndpoints.dealTasks(dealId), payload);
};

export const updateTaskService = async (
  taskId: string,
  payload: TaskUpdatePayload
): Promise<IAPIResponse<Task>> => {
  return API.Patch<TaskUpdatePayload, Task>(dealEndpoints.taskById(taskId), payload);
};

export const reorderTasksService = async (
  dealId: string,
  taskIds: string[]
): Promise<IAPIResponse<{ ok: boolean; changed: number }>> => {
  return API.Post<{ task_ids: string[] }, { ok: boolean; changed: number }>(`${dealEndpoints.dealTasks(dealId)}/reorder`, { task_ids: taskIds });
};

export const deleteTaskService = async (taskId: string): Promise<IAPIResponse<{ ok: boolean }>> => {
  return API.Delete<undefined, { ok: boolean }>(dealEndpoints.taskById(taskId));
};

export const nudgeTaskService = async (dealId: string, taskId: string, message?: string): Promise<IAPIResponse<{ owner: string; email: string; sms: string }>> => {
  return API.Post<{ message?: string }, { owner: string; email: string; sms: string }>(dealEndpoints.taskNudge(dealId, taskId), message ? { message } : {});
};

// ============ Documents ============
export const getDocumentsService = async (dealId: string): Promise<DealDocument[]> => {
  const response = await API.Get<DealDocument[]>(dealEndpoints.dealDocuments(dealId));
  if (response.data) return response.data;
  throw new Error(response.message);
};

export const addDocumentService = async (
  dealId: string,
  payload: DocumentFormData
): Promise<IAPIResponse<DealDocument>> => {
  return API.Post<DocumentFormData, DealDocument>(dealEndpoints.dealDocuments(dealId), payload);
};

export const updateDocumentService = async (
  docId: string,
  payload: { audience: Audience }
): Promise<IAPIResponse<DealDocument>> => {
  return API.Patch<{ audience: Audience }, DealDocument>(dealEndpoints.documentById(docId), payload);
};

export const deleteDocumentService = async (docId: string): Promise<IAPIResponse<{ ok: boolean }>> => {
  return API.Delete<undefined, { ok: boolean }>(dealEndpoints.documentById(docId));
};

// ============ Activity ============
export const getActivityService = async (dealId: string): Promise<Activity[]> => {
  const response = await API.Get<Activity[]>(dealEndpoints.dealActivity(dealId));
  if (response.data) return response.data;
  throw new Error(response.message);
};

export const postActivityService = async (
  dealId: string,
  payload: ActivityFormData
): Promise<IAPIResponse<{ ok: boolean }>> => {
  return API.Post<ActivityFormData, { ok: boolean }>(dealEndpoints.dealActivity(dealId), payload);
};

// ============ Email log ============
export const getDealEmailsService = async (dealId: string, page = 1): Promise<DealEmailListResponse> => {
  const response = await API.Get<DealEmailListResponse>(`${dealEndpoints.dealEmails(dealId)}?page=${page}&limit=25`);
  if (response.data) return response.data;
  throw new Error(response.message);
};

// ============ Public microsite ============
export const getPublicDealService = async (slug: string): Promise<PublicDealResponse> => {
  const response = await API.Get<PublicDealResponse>(dealEndpoints.publicDealBySlug(slug));
  if (response.data) return response.data;
  throw new Error(response.message);
};

/** The in-app path behind a texted `/u/<code>` link. */
export const resolveShortLinkService = async (code: string): Promise<string> => {
  const response = await API.Get<{ path: string }>(dealEndpoints.shortLink(code));
  if (response.data?.path) return response.data.path;
  throw new Error(response.message);
};


export const getAppointmentsService = async (dealId: string, includePast = false): Promise<Appointment[]> => {
  const response = await API.Get<{ appointments: Appointment[] }>(`${dealEndpoints.dealAppointments(dealId)}?include_past=${includePast}`);
  if (response.data) return response.data.appointments;
  throw new Error(response.message);
};
export const postAppointmentService = (dealId: string, payload: AppointmentPayload) =>
  API.Post<AppointmentPayload, Appointment>(dealEndpoints.dealAppointments(dealId), payload);
export const patchAppointmentService = (appointmentId: string, payload: AppointmentPayload) =>
  API.Patch<AppointmentPayload, Appointment>(dealEndpoints.appointment(appointmentId), payload);
export const cancelAppointmentService = (appointmentId: string, expectedSequence: number) =>
  API.Post<{ expected_sequence: number }, Appointment>(dealEndpoints.appointmentCancel(appointmentId), { expected_sequence: expectedSequence });

export const getAppointmentAttendeesService = async (dealId: string): Promise<AppointmentAttendee[]> => {
  const response = await API.Get<{ attendees: AppointmentAttendee[] }>(dealEndpoints.appointmentAttendees(dealId));
  if (response.data) return response.data.attendees;
  throw new Error(response.message);
};
